import { useCallback, useEffect, useState } from "react";
import { cn } from "@/lib/styles.utils";
import { useTranslate } from "@/hooks/use-translate/useTranslate";
import { Icon } from "@/components/icons/Icon";
import type { InspectionState } from "../InspectionsLists.d";
import InspectionsSearchers from "./InspectionsSearchers";
import InspectionsMobileSorter from "./InspectionsMobileSorter";

export interface InspectionsMobileSearcherProps {
  tableType: InspectionState;
  disabled: boolean;
  searchInspections: (params: string) => void;
  getNoSortedInspections: () => void;
  cleanSearch: () => void;
  openedCreator: boolean;
  lastFiltered: string;
}

const InspectionsMobileSearcher = ({
  tableType,
  disabled,
  searchInspections,
  getNoSortedInspections,
  cleanSearch,
  openedCreator,
  lastFiltered,
}: InspectionsMobileSearcherProps) => {
  const translate = useTranslate();

  const [searchers, setSearchers] = useState(false);
  const [currentTable, setCurrentTable] = useState(tableType);
  const [sorters, setSorters] = useState(false);
  const [lastSort, setLastSort] = useState<{
    sort: string;
    direction: "asc" | "desc";
  } | null>(null);

  useEffect(() => {
    if (!lastFiltered.length) setLastSort(null);
  }, [lastFiltered]);

  const showSearchers = useCallback(() => {
    if (!searchers) {
      setSearchers(true);
    } else {
      setSearchers(false);
    }
  }, [searchers]);

  const showSorters = useCallback(() => {
    setSorters((previousSorter) => !previousSorter);
  }, []);

  useEffect(() => {
    if (currentTable !== tableType) {
      if (searchers) {
        cleanSearch();
        setLastSort(null);
        setSearchers(false);
      }
      setCurrentTable(tableType);
    }
    if (openedCreator && searchers) {
      cleanSearch();
      setLastSort(null);
      setSearchers(false);
    }
  }, [tableType, searchers, currentTable, openedCreator, cleanSearch]);

  return (
    <>
      <div className="h-16 flex justify-end items-center w-full gap-x-6">
        <button
          type="button"
          className="flex flex-row h-8 px-3 gap-x-2 items-center"
          onClick={showSearchers}
          disabled={disabled}
        >
          <Icon
            name={searchers ? "closeEmptyCircle" : "searchLens"}
            className={cn(
              "size-4",
              searchers ? "text-ib-red-main" : "",
              !searchers
                ? `${!disabled ? "text-ib-black" : "text-ib-gray-dark-2"}`
                : "",
            )}
          />
          <span
            className={cn(
              "pb-[1px]",
              searchers ? "text-ib-red-main" : "",
              !searchers
                ? `${!disabled ? "text-ib-gray-darkest" : "text-ib-gray-dark-2"}`
                : "",
            )}
          >
            {translate("search")}
          </span>
        </button>
        <button
          type="button"
          className="flex flex-row h-8 px-3 gap-x-2 items-center"
          onClick={showSorters}
          disabled={disabled}
        >
          <Icon
            name="sortArrows"
            className={cn(
              "size-4",
              lastSort?.sort ? "text-ib-red-main" : "",
              !lastSort?.sort
                ? `${!disabled ? "text-ib-black" : "text-ib-gray-dark-2"}`
                : "",
            )}
          />
          <span
            className={cn(
              "pb-[1px]",
              lastSort?.sort ? "text-ib-red-main" : "",
              !lastSort?.sort
                ? `${!disabled ? "text-ib-gray-darkest" : "text-ib-gray-dark-2"}`
                : "",
            )}
          >
            {translate("sort")}
          </span>
        </button>
      </div>
      <InspectionsSearchers
        tableType={tableType}
        rendered={searchers}
        searchInspections={searchInspections}
        lastFiltered={lastFiltered}
        cleanLastSort={() => setLastSort(null)}
      />
      {sorters ? (
        <InspectionsMobileSorter
          lastFiltered={lastFiltered}
          close={showSorters}
          searchInspections={searchInspections}
          getNoSortedInspections={getNoSortedInspections}
          tableType={tableType}
          lastSort={lastSort}
          updateLastSort={(data) => setLastSort(data)}
        />
      ) : null}
    </>
  );
};
export default InspectionsMobileSearcher;
